import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {BehaviorSubject, forkJoin, Observable, of, switchMap} from 'rxjs';
import {environment} from '../../../../environments/environment';
import {GlobalConstants} from 'src/app/shared/global-constants';
import {CommonService} from 'src/app/shared/common.service';
import {getStoredJwtPayload} from '../../../shared/token/jwt.helper';
import {UserAuthoritiesService, UserTypeGroup} from '../../../shared/user';
import {OrganizationService} from '../../organization-group/services/organization.service';
import {ProductGroupService} from '../../organization-group/services/product-group.service';
import {CreateProductGroupRequest, OrgUnitResponse} from '../../organization-group/models/org-unit.models';

@Injectable({
  providedIn: 'root',
})
export class UserManagementService {
  private readonly users = new BehaviorSubject([]);
  private readonly orgList = new BehaviorSubject([]);
  treeMap;
  listUser = [];

  constructor(
    private readonly http: HttpClient,
    private readonly service: CommonService,
    private readonly userAuthoritiesService: UserAuthoritiesService,
    private readonly organizationService: OrganizationService,
    private readonly productGroupService: ProductGroupService,
  ) {}

  userListTableColDef(): any[] {
    return [
      { field: 'checkbox', type: 'checkbox', colWidth: '2%' },
      { field: 'name', header: 'Name', type: 'link', colWidth: '15%' },
      { field: 'email', header: 'Email', type: 'text', colWidth: '20%' },
      { field: 'roleName', header: 'Roles', type: 'text' },
      {
        field: 'serviceRegion',
        header: 'Service Region',
        type: 'text',
      },
      {
        field: 'productGroup',
        header: 'Product Group',
        type: 'text',
      },
      {
        field: 'lastLoggedOn',
        header: 'Last Logged On(Future)',
        type: 'date',
        isActive: false,
      },
    ];
  }

  userListDetailsColDef(): any[] {
    return [
      { field: 'checkbox', type: 'checkbox', colWidth: '2%' },
      { field: 'name', header: 'Name', type: 'link', colWidth: '20%' },
      { field: 'email', header: 'Email', type: 'text', colWidth: '30%' },
      { field: 'roleName', header: 'Roles', type: 'text', colWidth: '10%' },

      {
        field: 'lastLogegdOn',
        header: 'Last Logged On(Future)',
        type: 'date',
        isActive: false,
      },
      {
        field: 'status',
        header: 'Status',
        type: 'switch',
        colWidth: '15%',
      },
    ];
  }

  public deleteUsers(userList): Observable<any> {
    const response: any = [];

    userList.forEach((user) => {
      response.push(
        this.http.delete(
          environment.v2ApiBaseUrl + environment.deleteUser + user.id
        )
      );
    });
    return forkJoin(response);
  }

  public updateUsers(userList, data): Observable<any> {
    const response: any = [];
    response.push(
      this.http.put(
        environment.v2ApiBaseUrl + environment.updateUserName + userList.id,
        data
      )
    );
    return forkJoin(response);
  }

  setUpdatedUserList(data: any): void {
    this.users.next(data);
  }

  getMultipleOrgUserData(userId): Observable<any> {
    const apiUrl = `${environment.baseUrl}users/pid`;
    const params = new HttpParams().append('uuid', userId);
    return this.http.get(apiUrl, { params });
  }

  getUsers(): Observable<any> {
    const apiUrl = `${environment.baseUrl}users`;
    return this.http.get(apiUrl);
  }

  userList(): any {
    const apiUrl = environment.v2ApiBaseUrl + environment.queryApi;

    const reqBody = {
      child: 'User',
      parent: '',
      childId: '',
      parentId: '',
      projectParent: true,
      condition: { operator: 'and' },
    };
    const userList = this.http.post(apiUrl, reqBody);
    userList.subscribe((data) => {

        for (const j in data) {
          this.listUser.push(data[j].User);
        }
        return this.listUser;

      });
  }
  getOrgListData(): Observable<OrgUnitResponse[]> {
    return this.organizationService.getOrganizations();
  }

  setUpdatedOrgList(data: OrgUnitResponse[]): void {
    const groupHierarchy = data
      .map((e) => ({ domain: e }))
      .filter((x) => x.domain !== null);
    this.orgList.next(groupHierarchy);
  }

  getUpdatedOrgList(): Observable<any> {
    return this.orgList.asObservable();
  }

  getOrgDetailsData(selection: string): Observable<OrgUnitResponse[]> {
    return this.organizationService.searchOrganizations(selection);
  }

  getOrgDetails(): Observable<any> {
    const url = environment.v2ApiBaseUrl + environment.queryApi;

    const reqBody = {
      child: 'OU',
      parent: '',
      childId: '',
      parentId: '',
      projectParent: true,
      condition: { operation: 'and', criteria: [] },
    };

    return this.http.post(url, reqBody);
  }

  createOrgUnit(reqBody: CreateProductGroupRequest): Observable<OrgUnitResponse> {
    if (reqBody.category === 'ProductGroup') {
      return this.productGroupService.createProductGroup(reqBody);
    }
    return this.organizationService.createOrganization(reqBody);
  }

  updateServiceRegion(previousValues, req): Observable<any> {
    const attributes: any = {};
    if (previousValues.description !== req.serviceGroupDesc) {
      attributes.description = req.serviceGroupDesc;
    }
    return Object.keys(attributes).length > 0
      ? this.organizationService.updateOrganization(req.serviceGroupUuid, { attributes })
      : of([]);
  }

  updatePoductGroup(previousValues, currentValues): Observable<any> {
    const response: any = [];
    const editedProductGrps = this.detectChangesInProductGrop(previousValues, currentValues);

    editedProductGrps.forEach((productGroup) => {
      const reqBody = { attributes: productGroup.attribute };
      response.push(this.organizationService.updateOrganization(productGroup.uuid, reqBody));
    });
    return response.length > 0 ? forkJoin(response) : of([]);
  }

  detectChangesInProductGrop(previousValues, currentValues): any {
    const editedProductGrps = [];
    for (let i = 0; i < previousValues.length; i++) {
      if (
        previousValues[i].emailId !== currentValues[i].email ||
        previousValues[i].description !== currentValues[i].description
      ) {
        const req = {
          uuid: currentValues[i].uuid,
          attribute: {} as any,
        };

        if (previousValues[i]['OU.name'] !== currentValues[i].name) {
          req.attribute.name = currentValues[i].name;
        }
        if (previousValues[i].emailId !== currentValues[i].email) {
          req.attribute.emailId = currentValues[i].email;
        }
        if (previousValues[i].description !== currentValues[i].description) {
          req.attribute.description = currentValues[i].description;
        }
        editedProductGrps.push(req);
      }
    }
    return editedProductGrps;
  }

  public deleteOrganisation(OUlist, delSR): Observable<any> {
    const response: any = [];
    OUlist.forEach((OU) => {
      response.push(this.organizationService.deleteOrganization(OU.id));
    });
    return forkJoin(response);
  }

  prepareOrgTreeMap(serviceRegions: any, productGroups: any): any {
    this.treeMap = new Map();
    serviceRegions.forEach((serviceRegion) => {
      const children = new Map();
      productGroups.forEach((productGroup) => {
        if (
          !serviceRegion.id.includes(productGroup) &&
          !this.treeMap.has(serviceRegion.name)
        ) {
          this.treeMap.set(serviceRegion.name, serviceRegion.id);
        } else if (
          serviceRegion.id === productGroup.parentOuId &&
          this.treeMap.has(serviceRegion.name)
        ) {
          children.set(productGroup.name, productGroup.id);
          this.treeMap.set(serviceRegion.name, children);
        }
      });
    });
    return this.treeMap;
  }

  prepareTreeNodes(tree): any {
    let orgTreeNodes = [];
    for (const key1 of tree.keys()) {
      orgTreeNodes = this.prepareLevel1Nodes(tree, key1, orgTreeNodes);
    }
    return orgTreeNodes;
  }

  prepareLevel1Nodes(tree, key1, orgTreeNodes): any {
    const l1nodedata = tree.get(key1);
    if (l1nodedata.length === undefined) {
      let l2nodes = [];
      l2nodes = this.prepareLevel2Nodes(l1nodedata);
      const l1node = {
        label: key1,
        data: key1,
        key: key1,
        children: l2nodes,
      };
      orgTreeNodes.push(l1node);
    } else {
      const l1node = {
        label: key1,
        data: key1,
        key: key1,
      };
      orgTreeNodes.push(l1node);
    }
    return orgTreeNodes;
  }

  prepareLevel2Nodes(l1nodedata: any): any {
    const l2nodes = [];
    for (const key2 of l1nodedata.keys()) {
      const l2nodedata = l1nodedata.get(key2);
      if (l2nodedata) {
        const l2node = {
          label: key2,
          data: key2,
          key: key2,
        };
        l2nodes.push(l2node);
      }
    }

    return l2nodes;
  }

  changeUserStatus(userUUID: any, status: any): Observable<any> {
    const url =
      environment.v1ApiBaseUrl + environment.updateUserStatus + userUUID;
    const body = {
      enable: status,
    };
    return this.http.put(url, body);
  }

  getOrgTreeMap(): any {
    return this.treeMap;
  }

  assignOrgSelection(orgList, userDetailData: [], allUserDomain): any {
    const selectedFiles: any = [];
    const arrObj = [];
    const OUList = allUserDomain.assignedOUList.split(
      GlobalConstants.MULTI_REGION_SEPERATOR
    );
    for (const item of orgList) {
      for (const userOrgObj of OUList) {
        selectedFiles.push(this.mapItems(arrObj, item, userOrgObj));
      }
    }
    return of(selectedFiles.flat(1));
  }

  mapItems(arr: any, item: any, ouId: any): any {
    const obj = arr;
    let allOUData: any;
    item.forEach((element) => {
      if (
        element.hasOwnProperty('children') &&
        element.hasOwnProperty('data')
      ) {
        if (ouId.length > 0) {
          allOUData = ouId.split(GlobalConstants.SEPARATOR);
          if (element.key === allOUData[0]) {
            obj.push(element);
            element.children.forEach((childElement: any) => {
              if (childElement.label === allOUData[1]) {
                obj.push(childElement);
              }
            });
          }
        }
      }

    });
    return obj;
  }

  parentPartialCheck(orgList: any, selectedArr: any): any {
    selectedArr.forEach((element: any) => {
      if (element.isParentAvail === false) {
        let count = 0;
        selectedArr.find((item: any) => {
          if (element.key === item.parent) {
            count++;
          }
        });

        if (element.children.length == count) {
        } else {
          element.partialSelected = true;
        }
      }
    });
    return orgList;
  }

  getroleVal(role): any {
    const str = role;
    const rolearr = str.split('_', 4);
    let roleVal = '';
    if (rolearr.length === 2) {
      roleVal = rolearr[1];
    }
    if (rolearr.length === 3) {
      roleVal = `${rolearr[1]} ${rolearr[2]}`;
    } else {
      return (roleVal = role);
    }
    return roleVal;
  }

  getOUDetailedList(): Observable<OrgUnitResponse[]> {
    return this.organizationService.getOrganizations();
  }

  getAllUserOU(ID): Observable<any> {
    const url = environment.v3ApiBaseUrl + environment.getDomain + '/' + ID;
    return this.http.get(url);
  }
  getProductGroupsByServiceRegion(parentId: string): Observable<OrgUnitResponse[]> {
    return this.productGroupService.getProductGroupsByServiceRegion(parentId);
  }

  getUserType(): Observable<any> {
    const decodedPayload = getStoredJwtPayload();
    return this.userAuthoritiesService.getAuthorities()
      .pipe(
        switchMap(response => {
          if (UserTypeGroup.isCustomerUser(response.authorities)) {
            return this.http.post(`${environment.baseUrl}${environment.getUserType}`, {username: decodedPayload.name});
          }
          return this.http.post(`${environment.baseUrl}${environment.getUserType}`, {username: decodedPayload.uuid});
        })
      );
  }
}
